import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import HeaderBar from "@/auth/HeaderBar";
import Footer from "@/components/landing/Footer";
import { useCart } from "@/cart/CartProvider";
import { Button } from "@/components/ui/button";
import {
    ArrowLeft,
    Minus,
    Plus,
    ShoppingCart,
    Users,
    Check,
    Sparkles,
    X,
    ZoomIn,
} from "lucide-react";
import { toast } from "sonner";

export default function MenuPage() {
    const navigate = useNavigate();
    const { meals, cart, updateQty, familyConfig, addFamilyMeal } = useCart();
    const [featuredId, setFeaturedId] = useState(null);

    // Set featured from URL hash or default to first meal once meals load
    useEffect(() => {
        if (!meals?.length) return;
        const hash = window.location.hash?.replace("#", "");
        const found = hash && meals.find((m) => m.id === hash);
        setFeaturedId((prev) => prev || found?.id || meals[0].id);
    }, [meals]);

    const featured = meals.find((m) => m.id === featuredId);
    const related = meals.filter((m) => m.id !== featuredId);
    const relatedThisWeek = related.filter((m) => m.in_weekly_menu !== false);
    const relatedMoreOptions = related.filter((m) => m.in_weekly_menu === false);

    const handleSelect = (id) => {
        setFeaturedId(id);
        window.history.replaceState({}, "", `/menu#${id}`);
        const top = document.getElementById("featured-meal");
        if (top) top.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-cream text-navy font-sans pb-32">
            {/* Compact hero — no more overlap */}
            <div className="relative hero-gradient grain overflow-hidden">
                <HeaderBar onSignIn={() => {}} dark />
                <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-28 pb-12">
                    <Link
                        to="/"
                        data-testid="menu-back-home"
                        className="inline-flex items-center gap-1.5 text-mint hover:text-white text-sm font-bold transition-colors"
                    >
                        <ArrowLeft size={14} /> Back to home
                    </Link>
                    <p className="mt-4 text-gold tracking-[0.3em] text-[11px] font-bold uppercase">
                        Full Menu · This Week
                    </p>
                    <h1 className="mt-2 font-serif font-black text-white text-3xl sm:text-4xl md:text-5xl leading-tight">
                        Build your{" "}
                        <span className="text-gold italic">box</span> — your way.
                    </h1>
                </div>
            </div>

            {/* Featured meal detail */}
            {featured && (
                <FeaturedMeal
                    meal={featured}
                    qty={cart[featured.id] || 0}
                    onQtyChange={(q) => updateQty(featured.id, q)}
                />
            )}

            {/* Related dishes — This Week */}
            {relatedThisWeek.length > 0 && (
                <section
                    data-testid="related-meals-section"
                    className="max-w-5xl mx-auto px-6 sm:px-8 mt-12"
                >
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
                        <h2 className="font-serif font-black text-2xl sm:text-3xl text-navy">
                            This week's menu
                        </h2>
                        <p className="text-xs text-muted2 uppercase tracking-widest font-bold">
                            {relatedThisWeek.length} more this week
                        </p>
                    </div>
                    <ul className="space-y-3">
                        {relatedThisWeek.map((m) => (
                            <RelatedMealRow
                                key={m.id}
                                meal={m}
                                qty={cart[m.id] || 0}
                                onSelect={() => handleSelect(m.id)}
                                onQtyChange={(q) => updateQty(m.id, q)}
                            />
                        ))}
                    </ul>
                </section>
            )}

            {/* Related dishes — More Options (Min 3 per item) */}
            {relatedMoreOptions.length > 0 && (
                <section
                    data-testid="more-options-section"
                    className="max-w-5xl mx-auto px-6 sm:px-8 mt-12"
                >
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                        <h2 className="font-serif font-black text-2xl sm:text-3xl text-navy">
                            More options
                        </h2>
                        <span className="px-2.5 py-1 rounded-full bg-burnt text-white text-[10px] font-bold uppercase tracking-widest">
                            Min 3 per item
                        </span>
                    </div>
                    <p className="text-sm text-muted2 mb-5">
                        Not in this week's menu but still on the table — order
                        any of these as long as you take <strong>3 of the
                        same dish</strong> per order.
                    </p>
                    <ul className="space-y-3">
                        {relatedMoreOptions.map((m) => (
                            <RelatedMealRow
                                key={m.id}
                                meal={m}
                                qty={cart[m.id] || 0}
                                onSelect={() => handleSelect(m.id)}
                                onQtyChange={(q) => updateQty(m.id, q)}
                            />
                        ))}
                    </ul>
                </section>
            )}

            {/* Related family option */}
            {familyConfig && meals?.length > 0 && (
                <RelatedFamily
                    meals={meals}
                    config={familyConfig}
                    initialMealId={featuredId}
                    onAdd={addFamilyMeal}
                />
            )}

            <Footer />
        </div>
    );
}

/* ───── Featured meal ───── */
function FeaturedMeal({ meal, qty, onQtyChange }) {
    const lineTotal = +(meal.price * Math.max(qty, 1)).toFixed(2);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const hasSignature = Boolean(meal.signature_image);

    return (
        <section
            id="featured-meal"
            data-testid={`featured-meal-${meal.id}`}
            className="max-w-5xl mx-auto px-6 sm:px-8 -mt-6"
        >
            <article className="relative bg-white rounded-[24px] p-6 sm:p-8 shadow-[0_18px_50px_rgba(13,40,24,0.12)] border border-mint">
                <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-gold text-navy text-[10px] font-bold uppercase tracking-widest shadow-md inline-flex items-center gap-1">
                    <Sparkles size={11} /> Your pick
                </div>

                <div className="flex items-start justify-between gap-3 flex-wrap mt-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase text-white"
                            style={{ background: meal.diet_tag_color }}
                        >
                            {meal.diet_tag}
                        </span>
                        <span className="text-xl">{meal.cuisine_emoji}</span>
                    </div>
                    {meal.is_premium && meal.in_weekly_menu === false ? (
                        <span className="px-2 py-0.5 rounded-full bg-navy text-gold text-[10px] font-bold uppercase tracking-widest">
                            Premium · Min {meal.effective_min_quantity || 3}
                        </span>
                    ) : meal.is_premium ? (
                        <span className="px-2 py-0.5 rounded-full bg-navy text-gold text-[10px] font-bold uppercase tracking-widest">
                            Premium
                        </span>
                    ) : meal.in_weekly_menu === false ? (
                        <span className="px-2 py-0.5 rounded-full bg-burnt text-white text-[10px] font-bold uppercase tracking-widest">
                            Off-menu · Min {meal.effective_min_quantity || 3}
                        </span>
                    ) : meal.original_price && meal.original_price > meal.price ? (
                        <span className="px-2 py-0.5 rounded-full bg-burnt text-white text-[10px] font-bold uppercase tracking-widest">
                            Launch deal
                        </span>
                    ) : null}
                </div>

                {/* Title row — with optional signature image floating beside title */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-start">
                    <div className="min-w-0">
                        <h2 className="font-serif font-black text-navy text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
                            {meal.name}
                        </h2>
                        <p className="mt-1.5 text-muted2 italic text-sm sm:text-base">
                            {meal.subtitle}
                        </p>
                    </div>
                    {hasSignature && (
                        <button
                            type="button"
                            data-testid={`featured-${meal.id}-signature-trigger`}
                            onClick={() => setLightboxOpen(true)}
                            aria-label={`Open signature photo of ${meal.name}`}
                            className="relative shrink-0 self-start group rounded-2xl overflow-hidden border-2 border-mint shadow-[0_10px_30px_rgba(13,40,24,0.18)] transition-all duration-200 hover:shadow-[0_14px_36px_rgba(13,40,24,0.28)] hover:-translate-y-0.5 w-full md:w-56 bg-cream"
                        >
                            <img
                                src={meal.signature_image}
                                alt={`${meal.name} signature plate`}
                                className="block w-full h-56 md:h-56 object-contain"
                                draggable={false}
                            />
                            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-gold text-navy text-[9px] font-bold uppercase tracking-widest shadow-sm">
                                Signature
                            </span>
                            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                <ZoomIn size={11} /> Tap to enlarge
                            </span>
                        </button>
                    )}
                </div>

                <p className="mt-5 text-[15px] sm:text-base leading-relaxed text-navy/85 max-w-2xl">
                    {meal.description}
                </p>

                {/* Ingredients */}
                <div className="mt-6">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-muted2 mb-2.5">
                        🌿 What's inside
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {meal.ingredients.map((ing, i) => (
                            <span
                                key={i}
                                className="px-3 py-1.5 rounded-full bg-mint text-forest text-[13px] font-medium"
                            >
                                {ing}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Nutrition */}
                <div className="mt-6 grid grid-cols-4 gap-2 bg-[#F7F5EF] rounded-2xl p-3">
                    <NutritionCell
                        icon="🔥"
                        value={meal.nutrition.calories}
                        label="Cal"
                        color="text-burnt"
                    />
                    <NutritionCell
                        icon="💪"
                        value={`${meal.nutrition.protein}g`}
                        label="Protein"
                    />
                    <NutritionCell
                        icon="🍚"
                        value={`${meal.nutrition.carbs}g`}
                        label="Carbs"
                    />
                    <NutritionCell
                        icon="🫒"
                        value={`${meal.nutrition.fat}g`}
                        label="Fat"
                    />
                </div>

                {/* Price + qty + add */}
                <div className="mt-7 pt-6 border-t border-mint flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-muted2 font-bold">
                            Per Meal
                        </p>
                        <div className="flex items-baseline gap-2">
                            <p className="font-display text-forest text-5xl leading-none tracking-wide">
                                ${meal.price.toFixed(2)}
                            </p>
                            {!meal.is_premium &&
                                meal.original_price &&
                                meal.original_price > meal.price && (
                                    <p className="font-display text-muted2 text-2xl leading-none line-through decoration-2 decoration-burnt/70">
                                        ${meal.original_price.toFixed(2)}
                                    </p>
                                )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <FeaturedStepper
                            qty={qty}
                            onChange={onQtyChange}
                            testId={meal.id}
                            meal={meal}
                        />
                        <Button
                            data-testid={`featured-${meal.id}-add-btn`}
                            onClick={() => {
                                const minQty = Math.max(
                                    meal.effective_min_quantity || 1,
                                    1,
                                );
                                if (qty === 0 && minQty > 1) {
                                    onQtyChange(minQty);
                                    const label = meal.is_premium
                                        ? "Premium"
                                        : "Off-menu";
                                    toast.success(
                                        `${minQty}× ${meal.name} added · ${label} min ${minQty}`,
                                    );
                                } else {
                                    onQtyChange((qty || 0) + 1);
                                    toast.success(`${meal.name} added to order`);
                                }
                            }}
                            className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-5 py-5 h-auto flex items-center gap-1.5 shadow-[0_10px_28px_rgba(244,164,53,0.35)]"
                        >
                            <ShoppingCart size={16} />
                            {qty > 0
                                ? `Add another · $${meal.price.toFixed(2)}`
                                : (meal.effective_min_quantity || 1) > 1
                                ? `Add ${meal.effective_min_quantity} · $${(meal.price * meal.effective_min_quantity).toFixed(2)}`
                                : `Add to order · $${meal.price.toFixed(2)}`}
                        </Button>
                    </div>
                </div>

                {qty > 0 && (
                    <div className="mt-4 flex items-center justify-end gap-2 text-sm text-forest font-bold">
                        <Check size={14} /> {qty}× in your order ·{" "}
                        <span className="font-display text-lg">
                            ${lineTotal.toFixed(2)}
                        </span>
                    </div>
                )}
            </article>

            {/* Lightbox modal for signature image */}
            {hasSignature && lightboxOpen && (
                <SignatureLightbox
                    src={meal.signature_image}
                    alt={`${meal.name} signature plate`}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </section>
    );
}

function SignatureLightbox({ src, alt, onClose }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <div
            data-testid="signature-lightbox"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
        >
            <button
                data-testid="signature-lightbox-close"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
            >
                <X size={20} />
            </button>
            <img
                src={src}
                alt={alt}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[88vh] max-w-[92vw] rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] bg-cream"
                draggable={false}
            />
        </div>
    );
}

function FeaturedStepper({ qty, onChange, testId, meal }) {
    const minQty = Math.max(meal?.effective_min_quantity || 1, 1);
    const handleIncrement = () => {
        if (qty === 0 && minQty > 1) onChange(minQty);
        else onChange(qty + 1);
    };
    const handleDecrement = () => {
        if (qty <= minQty) onChange(0);
        else onChange(qty - 1);
    };
    return (
        <div className="inline-flex items-center gap-1 bg-cream rounded-full border border-mint p-1">
            <button
                data-testid={`featured-${testId}-decrement`}
                onClick={handleDecrement}
                disabled={qty === 0}
                aria-label="decrease quantity"
                className="w-10 h-10 rounded-full flex items-center justify-center text-forest hover:bg-mint disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
                <Minus size={16} />
            </button>
            <span
                data-testid={`featured-${testId}-qty`}
                className="font-display text-2xl text-navy w-7 text-center tabular-nums"
            >
                {qty}
            </span>
            <button
                data-testid={`featured-${testId}-increment`}
                onClick={handleIncrement}
                aria-label="increase quantity"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-forest text-white hover:bg-sage transition-colors active:scale-95"
            >
                <Plus size={16} />
            </button>
        </div>
    );
}

function NutritionCell({ icon, value, label, color = "text-navy" }) {
    return (
        <div className="text-center">
            <div className="text-base leading-none">{icon}</div>
            <div className={`mt-1 font-bold text-[15px] ${color}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted2">
                {label}
            </div>
        </div>
    );
}

/* ───── Related meal row ───── */
function RelatedMealRow({ meal, qty, onSelect, onQtyChange }) {
    return (
        <li
            data-testid={`related-meal-${meal.id}`}
            className="bg-white rounded-[18px] p-3 sm:p-4 border border-mint shadow-[0_4px_18px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.10)] transition-all"
        >
            <div className="flex items-center gap-3 sm:gap-4">
                <button
                    type="button"
                    onClick={onSelect}
                    data-testid={`related-${meal.id}-select`}
                    className="flex-1 min-w-0 text-left flex items-center gap-3 sm:gap-4"
                >
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden flex-shrink-0 border border-mint bg-cream">
                        <img
                            src={meal.image}
                            alt={meal.name}
                            className="w-full h-full object-cover"
                            draggable={false}
                            loading="lazy"
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-serif font-black text-navy text-base sm:text-lg leading-tight truncate">
                            {meal.name}
                        </p>
                        <p className="text-[11px] sm:text-xs text-muted2 truncate">
                            {meal.subtitle}
                        </p>
                        <div className="mt-1 flex items-baseline gap-1.5">
                            <span className="font-display text-forest text-xl leading-none">
                                ${meal.price.toFixed(2)}
                            </span>
                            {!meal.is_premium &&
                                meal.original_price &&
                                meal.original_price > meal.price && (
                                    <span className="font-display text-muted2 text-sm line-through decoration-burnt/70">
                                        ${meal.original_price.toFixed(2)}
                                    </span>
                                )}
                            {meal.is_premium && meal.in_weekly_menu === false ? (
                                <span className="px-1.5 py-0.5 rounded-full bg-navy text-gold text-[9px] font-bold uppercase tracking-widest ml-1">
                                    Premium · Min {meal.effective_min_quantity || 3}
                                </span>
                            ) : meal.is_premium ? (
                                <span className="px-1.5 py-0.5 rounded-full bg-navy text-gold text-[9px] font-bold uppercase tracking-widest ml-1">
                                    Premium
                                </span>
                            ) : meal.in_weekly_menu === false ? (
                                <span className="px-1.5 py-0.5 rounded-full bg-burnt text-white text-[9px] font-bold uppercase tracking-widest ml-1">
                                    Min {meal.effective_min_quantity || 3}
                                </span>
                            ) : (
                                <span className="px-1.5 py-0.5 rounded-full bg-burnt/10 text-burnt text-[9px] font-bold uppercase tracking-widest ml-1">
                                    Deal
                                </span>
                            )}
                        </div>
                    </div>
                </button>
                <div className="flex-shrink-0">
                    <FeaturedStepper
                        qty={qty}
                        onChange={onQtyChange}
                        testId={meal.id + "-row"}
                        meal={meal}
                    />
                </div>
            </div>
        </li>
    );
}

/* ───── Related family ───── */
const FAMILY_CLICK_GUARD_MS = 2500;

function RelatedFamily({ meals, config, initialMealId, onAdd }) {
    const min = config.min_members ?? 4;
    const max = config.max_members ?? 15;
    // Premium meals can't be plated family-style — they have their own min-3 rule.
    const familyEligibleMeals = useMemo(
        () => meals.filter((m) => !m.is_premium),
        [meals],
    );
    const defaultId = familyEligibleMeals.find((m) => m.id === initialMealId)?.id
        || familyEligibleMeals[0]?.id
        || meals[0]?.id;
    const [mealId, setMealId] = useState(defaultId);
    const [members, setMembers] = useState(min);
    const [adding, setAdding] = useState(false);
    const lastClickAt = useRef(0);

    useEffect(() => {
        if (initialMealId && familyEligibleMeals.find((m) => m.id === initialMealId)) {
            setMealId(initialMealId);
        }
    }, [initialMealId, familyEligibleMeals]);

    const meal = familyEligibleMeals.find((m) => m.id === mealId)
        || familyEligibleMeals[0]
        || meals[0];
    if (!meal) return null;
    const price = +(meal.price * (members - 1)).toFixed(2);

    const add = () => {
        // Rapid-click guard — mobile touch events sometimes fire multiple
        // times in <100ms which had been turning 1 family plate into 4.
        const now = Date.now();
        if (adding || now - lastClickAt.current < FAMILY_CLICK_GUARD_MS) {
            return;
        }
        lastClickAt.current = now;
        const confirmMsg = `Add a family-size ${meal.name} for ${members} people at $${price.toFixed(2)}?\n\nThis is one large family plate that serves ${members} (not ${members} separate meals).`;
        if (typeof window !== "undefined" && !window.confirm(confirmMsg)) {
            return;
        }
        setAdding(true);
        onAdd({
            meal_id: meal.id,
            meal_name: meal.name,
            members,
            price,
        });
        toast.success(`Added: Family ${meal.name} · serves ${members} · $${price.toFixed(2)}`);
        setTimeout(() => setAdding(false), FAMILY_CLICK_GUARD_MS);
    };

    return (
        <section
            data-testid="related-family-section"
            className="max-w-5xl mx-auto px-6 sm:px-8 mt-14"
        >
            <div className="bg-white rounded-[24px] p-6 sm:p-8 border-2 border-burnt/30 shadow-[0_8px_28px_rgba(0,0,0,0.07)]">
                <div className="flex items-center gap-2">
                    <span className="w-9 h-9 rounded-xl bg-burnt/10 text-burnt flex items-center justify-center">
                        <Users size={16} />
                    </span>
                    <h2 className="font-serif font-black text-navy text-xl sm:text-2xl">
                        Feeding more than yourself?
                    </h2>
                </div>
                <p className="mt-2 text-muted2 text-sm sm:text-base max-w-xl">
                    Turn this same dish into a family-sized box. You only pay
                    for{" "}
                    <span className="font-bold text-navy">(people − 1)</span> —
                    great for Sunday dinner.
                </p>

                {/* Dish chip picker */}
                <div className="mt-5">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-muted2 mb-2">
                        Family dish
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {familyEligibleMeals.map((m) => {
                            const active = m.id === mealId;
                            return (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setMealId(m.id)}
                                    data-testid={`related-family-pick-${m.id}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border-2 ${
                                        active
                                            ? "bg-forest text-white border-forest"
                                            : "bg-white text-navy border-mint hover:border-forest"
                                    }`}
                                >
                                    {m.cuisine_emoji} {m.name}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Stepper + price */}
                <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                            How many people?
                        </p>
                        <div className="mt-2 flex items-center gap-2 bg-cream rounded-full p-1.5 border border-mint w-fit">
                            <button
                                data-testid="related-family-decrement"
                                onClick={() =>
                                    setMembers((m) => Math.max(min, m - 1))
                                }
                                disabled={members <= min}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-forest hover:bg-mint disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Minus size={16} />
                            </button>
                            <span className="font-display text-2xl text-navy w-10 text-center tabular-nums">
                                {members}
                            </span>
                            <button
                                data-testid="related-family-increment"
                                onClick={() =>
                                    setMembers((m) => Math.min(max, m + 1))
                                }
                                disabled={members >= max}
                                className="w-10 h-10 rounded-full flex items-center justify-center bg-forest text-white hover:bg-sage disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-[11px] uppercase tracking-widest font-bold text-muted2">
                            ${meal.price.toFixed(2)} × {members - 1}
                        </p>
                        <p className="font-display text-forest text-4xl sm:text-5xl tabular-nums leading-none">
                            ${price.toFixed(2)}
                        </p>
                    </div>
                </div>

                <Button
                    data-testid="related-family-add-btn"
                    onClick={add}
                    className="mt-5 w-full sm:w-auto rounded-full bg-gold hover:bg-goldDark text-navy font-bold py-5 px-7 flex items-center justify-center gap-2 shadow-[0_10px_28px_rgba(244,164,53,0.35)]"
                >
                    <ShoppingCart size={16} />
                    Add Family Plate to Order
                </Button>
            </div>
        </section>
    );
}
